import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Dialog, CircularProgress, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { getDefectPhotos } from '../redux/slice';
import './DefectCard.scss';

const DefectCard = ({ defect }) => {
	const { messages } = useIntl();
	const dispatch = useDispatch();
	const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
	const [photoUrls, setPhotoUrls] = useState({});
	const [loading, setLoading] = useState(false);

	if (!defect) return null;

	const getSeverityInfo = (severity) => {
		const severityMap = {
			255: { label: 'Safety Critical', color: '#d32f2f', icon: '▲' },
			192: { label: 'Pre-Trip Critical', color: '#f57c00', icon: '▲' },
			128: { label: 'Safety Advisory', color: '#fbc02d', icon: '▲' },
			127: { label: 'Safety Advisory', color: '#fbc02d', icon: '▲' },
			64: { label: 'Maintenance Required', color: '#1976d2', icon: '▲' },
		};
		return severityMap[severity] || { label: 'Unknown', color: '#757575', icon: '▲' };
	};

	const initialSeverity = getSeverityInfo(defect.severity);
	const repairs = defect.repairs || [];
	const latestRepair = repairs.length > 0 ? repairs[0] : null;
	const resolvedSeverity = latestRepair?.severity ? getSeverityInfo(latestRepair.severity) : null;

	const photos = defect.defectMedia || [];
	const previewUnavailableText = 'Preview unavailable — click to view full image';

	const resolvedPhotos = useMemo(() => {
		if (photos.length === 0) return [];

		return photos.map((photo) => ({
			...photo,
			resolvedUrl: (photo.fileKey && photoUrls[photo.fileKey]) || photo.url || photo.thumbnail,
		}));
	}, [photos, photoUrls]);

	const missingFileKeys = useMemo(() => {
		if (photos.length === 0) return [];

		const seen = new Set();
		return photos
			.map((photo) => photo.fileKey)
			.filter((key) => {
				if (!key || photoUrls[key] || seen.has(key)) return false;
				seen.add(key);
				return true;
			});
	}, [photos, photoUrls]);

	const handlePhotoClick = (index) => {
		setSelectedPhotoIndex(index);
	};

	const handleCloseDialog = () => {
		setSelectedPhotoIndex(null);
	};

	const handlePrevPhoto = () => {
		setSelectedPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
	};

	const handleNextPhoto = () => {
		setSelectedPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
	};

	useEffect(() => {
		if (selectedPhotoIndex === null) {
			setLoading(false);
			return undefined;
		}

		if (missingFileKeys.length === 0) {
			setLoading(false);
			return undefined;
		}

		let isCancelled = false;
		setLoading(true);

		dispatch(getDefectPhotos({ fileKeys: missingFileKeys }))
			.unwrap()
			.then((urls) => {
				if (!isCancelled && urls) {
					setPhotoUrls((prev) => ({
						...prev,
						...urls,
					}));
				}
			})
			.catch((error) => {
				if (!isCancelled) {
					console.error('[DefectCard] Failed to fetch defect photos', error);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [selectedPhotoIndex, missingFileKeys, dispatch]);

	const formatRepairDate = (dateString) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		return date.toLocaleString();
	};

	const currentResolvedSrc =
		selectedPhotoIndex === null
			? ''
			: resolvedPhotos[selectedPhotoIndex]?.resolvedUrl ||
			photos[selectedPhotoIndex]?.url ||
			photos[selectedPhotoIndex]?.thumbnail ||
			'';

	return (
		<Box className="defect-card">
			<Box className="defect-header">
				<Box className="severity-indicators">
					<Box
						className="severity-badge initial"
						style={{ backgroundColor: initialSeverity.color }}
						title={initialSeverity.label}
					>
						<svg className="severity-icon" viewBox="0 0 24 24" width="20" height="20">
							<path d="M12 2 L2 22 L22 22 Z" fill="white" />
						</svg>
					</Box>

					{resolvedSeverity && (
						<>
							<ArrowForwardIcon className="severity-arrow" />
							<Box
								className="severity-badge resolved"
								style={{ backgroundColor: resolvedSeverity.color }}
								title={resolvedSeverity.label}
							>
								<svg className="severity-icon" viewBox="0 0 24 24" width="20" height="20">
									<path d="M12 2 L2 22 L22 22 Z" fill="white" />
								</svg>
							</Box>
						</>
					)}
				</Box>
			</Box>

			<Box className="defect-body">
				<Typography variant="body1" className="condition-label">
					{defect.conditionName}
				</Typography>

				{defect.comment && (
					<Typography variant="body2" className="defect-comment">
						{defect.comment}
					</Typography>
				)}

				{photos.length > 0 && (
					<Box className="defect-photos">
						{photos.map((photo, index) => (
							<Box
								key={`photo-${photo.mediaId || index}`}
								className={`photo-thumbnail ${photo.url || photo.thumbnail ? '' : 'photo-placeholder'}`}
								onClick={() => handlePhotoClick(index)}
							>
								{photo.url || photo.thumbnail ? (
									<img
										src={photo.url || photo.thumbnail}
										alt={`Defect photo ${index + 1}`}
										loading="lazy"
									/>
								) : (
									<Box
										className="photo-placeholder-content"
										aria-label={`Defect photo ${index + 1}`}
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											width: '100%',
											height: '100%',
											textAlign: 'center',
											gap: '4px',
											px: 1,
										}}
									>
										<PhotoCameraIcon sx={{ fontSize: 28 }} />
										<Tooltip title={previewUnavailableText} placement="bottom" arrow>
											<Typography
												variant="caption"
												className="photo-placeholder-text"
												sx={{ fontSize: '10px', lineHeight: 1.1, maxWidth: 96 }}
											>
												{previewUnavailableText}
											</Typography>
										</Tooltip>
									</Box>
								)}
							</Box>
						))}
					</Box>
				)}

				{repairs.length > 0 && (
					<Box className="repair-info">
						<Typography variant="subtitle2" className="repair-title">
							{messages.repairInformation || 'Repair Information'}
						</Typography>

						{repairs.map((repair, index) => (
							<Box key={`repair-${repair.defectId}-${repair.createdOn || index}`} className="repair-entry">
								{repair.mechanicName && (
									<Box className="repair-field">
										<span className="field-label">{messages.mechanic || 'Mechanic'}:</span>
										<span className="field-value">{repair.mechanicName}</span>
									</Box>
								)}

								{repair.status && (
									<Box className="repair-field">
										<span className="field-label">{messages.status || 'Status'}:</span>
										<span className="field-value">{repair.status}</span>
									</Box>
								)}

								{repair.createdOn && (
									<Box className="repair-field">
										<span className="field-label">{messages.created || 'Created'}:</span>
										<span className="field-value">{formatRepairDate(repair.createdOn)}</span>
									</Box>
								)}

								{repair.resolution && (
									<Box className="repair-field">
										<span className="field-label">{messages.resolution || 'Resolution'}:</span>
										<span className="field-value">{repair.resolution}</span>
									</Box>
								)}

								{repair.workOrder && (
									<Box className="repair-field">
										<span className="field-label">{messages.workOrder || 'Work Order'}:</span>
										<span className="field-value">{repair.workOrder}</span>
									</Box>
								)}

								{repair.comment && (
									<Box className="repair-field">
										<span className="field-label">{messages.notes || 'Notes'}:</span>
										<span className="field-value">{repair.comment}</span>
									</Box>
								)}
							</Box>
						))}
					</Box>
				)}
			</Box>
			{/* Photo Dialog */}
			{selectedPhotoIndex !== null && photos.length > 0 && (
				<Dialog
					open={true}
					onClose={handleCloseDialog}
					maxWidth={false}
					className="photo-dialog"
					PaperProps={{
						style: {
							backgroundColor: 'rgba(0, 0, 0, 0.95)',
							boxShadow: 'none',
							width: '95vw',
							height: '95vh',
						},
					}}
				>
					<Box className="dialog-content">
						<IconButton className="close-button" onClick={handleCloseDialog}>
							<CloseIcon />
						</IconButton>
						<IconButton
							className="nav-button prev-button"
							onClick={handlePrevPhoto}
							disabled={photos.length <= 1}
						>
							<NavigateBeforeIcon />
						</IconButton>
						{!currentResolvedSrc && loading ? (
							<Box className="photo-loading-state">
								<CircularProgress color="primary" />
							</Box>
						) : !currentResolvedSrc ? (
							<Box className="photo-loading-state">
								<PhotoCameraIcon />
							</Box>
						) : (
							<img
								src={currentResolvedSrc}
								alt={`Photo ${selectedPhotoIndex + 1}`}
								className="dialog-image"
							/>
						)}
						<IconButton
							className="nav-button next-button"
							onClick={handleNextPhoto}
							disabled={photos.length <= 1}
						>
							<NavigateNextIcon />
						</IconButton>
						<Typography
							className="photo-info"
							style={{
								position: 'absolute',
								bottom: 8,
								left: '50%',
								transform: 'translateX(-50%)',
								color: '#fff',
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								padding: '4px 12px',
								borderRadius: '4px',
							}}
						>
							{selectedPhotoIndex + 1} / {photos.length}
						</Typography>
					</Box>
				</Dialog>
			)}
		</Box>
	);
};

export default DefectCard;
